import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";


import classes from "./createCouponFormPage.module.css";
import { useCreateCouponMutation } from "../../../services/coupon/couponMutationApiSlice";
import { FormInput } from "../../../component/shared/formElements/formInput/FormInput";
import { FormCalendar } from "../../../component/shared/formElements/formCalendar/FormCalendar";
import { FormRichText } from "../../../component/shared/formElements/formRichText/FormRichtext";
import { FormCheckboxChips } from "../../../component/shared/formElements/formCheckBoxChip/FormCheckBoxChip";

const categories = [
  { id: 5, label: "VRP" },
  { id: 6, label: "Spares" },
  { id: 7, label: "New Phones" },
  { id: 8, label: "Open Box" },
  { id: 9, label: "Prexo" },
];

export const CreateCouponFormPage = () => {
  const navigate = useNavigate();
  const [createCoupon, { isLoading: isSubmitting }] = useCreateCouponMutation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: {
      code: "",
      category_id: [],
      start_date: "",
      end_date: "",
      descrption: "",
      min_order_value: "",
      max_redeemable_amount: "",
      discount_value: "",
      discount_type: "",
    },
    mode: "onChange",
  });

  // Register ReactQuill manually for description validation
  useEffect(() => {
    register("descrption", {
      required: "Coupon description is required",
      validate: (val) => {
        const text = new DOMParser()
          .parseFromString(val || "", "text/html")
          .body.textContent.trim();
        return text.length > 0 || "Coupon description is required";
      },
    });
  }, [register]);

  const onSubmit = async (data) => {
    try {
      // Convert category IDs to number if needed
      data.category_id = data.category_id.map((id) => Number(id));

      // Optionally convert dates to timestamps if backend expects ms
      data.start_date = new Date(data.start_date).getTime();
      data.end_date = new Date(data.end_date).getTime();

      await createCoupon(data).unwrap();
      navigate("/coupons"); // redirect after success
    } catch (err) {
      console.error("Failed to create coupon:", err);
    }
  };

  return (
    <div className={classes.box}>
      <h1 className={classes.box__title}>Create Coupon</h1>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        {/* -------------------- BASIC INFO -------------------- */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Coupon Information</h3>
          <div className={classes.grid}>
            <FormInput
              id="code"
              label="Coupon Code"
              register={register}
              validation={{ required: "Coupon Code required" }}
              error={errors.code?.message}
              required
            />
            <FormCalendar
              id="start_date"
              label="Start Date"
              watch={watch}
              setValue={setValue}
              register={register}
              required
              error={errors.start_date?.message}
            />
            <FormCalendar
              id="end_date"
              label="End Date"
              watch={watch}
              setValue={setValue}
              register={register}
              required
              error={errors.end_date?.message}
            />
          </div>
        </div>

        {/* -------------------- DESCRIPTION -------------------- */}
        <div className={classes.section}>
          <FormRichText
            id="descrption"
            label="Coupon Description"
            value={watch("descrption")}
            onChange={(val) =>
              setValue("descrption", val, { shouldValidate: true })
            }
            required
            error={errors.descrption?.message}
          />
        </div>

        {/* -------------------- CATEGORIES -------------------- */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Applicable On Category</h3>
          <FormCheckboxChips
            id="category_id"
            label="Categories"
            options={categories}
            watch={watch}
            setValue={setValue}
            register={register}
            validation={{ required: "Category is required" }}
            error={errors.category_id?.message}
            required
          />
        </div>

        {/* -------------------- PRICING -------------------- */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Pricing Information</h3>
          <div className={classes.grid}>
            <FormInput
              id="min_order_value"
              label="Min Order Value"
              register={register}
              validation={{ required: "Min Order Value is required" }}
              error={errors.min_order_value?.message}
              required
            />
            <FormInput
              id="max_redeemable_amount"
              label="Max Redeemable Amount"
              register={register}
              validation={{ required: "Max Redeemable Amount is required" }}
              error={errors.max_redeemable_amount?.message}
              required
            />
            <FormInput
              id="discount_value"
              label="Discount Value"
              register={register}
              validation={{ required: "Discount Value is required" }}
              error={errors.discount_value?.message}
              required
            />
            <FormInput
              id="discount_type"
              label="Discount Type"
              register={register}
              validation={{ required: "Discount Type is required" }}
              error={errors.discount_type?.message}
              required
            />
          </div>
        </div>

        {/* -------------------- ACTIONS -------------------- */}
        <div className={classes.actions}>
          <button
            type="submit"
            className={classes.submitBtn}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Coupon"}
          </button>
        </div>
      </form>
    </div>
  );
};
